import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import {
  formatGroupLabel,
  validParentOptions,
} from "../utils/offer-group-hierarchy";

/**
 * Create / edit Offer::Group structure node (title, description→comment,
 * discipline, parent / «Flytt til…»). Persists via parent onSave callback.
 */
export default function OfferCalculationStructureGroupDialog({
  open,
  mode = "create",
  group = null,
  /** Prefill parent when creating a child */
  parentGroup = null,
  groups = [],
  saving = false,
  /** Raise above nested Edit-calc PMDialog (default 2000 is behind z=4200). */
  zIndex,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const isEdit = mode === "edit" && group?.id != null;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [groupType, setGroupType] = useState("");
  const [parentId, setParentId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (isEdit) {
      setTitle(group?.title || group?.discipline || "");
      setDescription(group?.comment || group?.description || "");
      setDiscipline(group?.discipline || "");
      setGroupType(group?.group_type || "");
      setParentId(
        group?.parent_id != null && group?.parent_id !== ""
          ? String(group.parent_id)
          : ""
      );
    } else {
      setTitle("");
      setDescription("");
      setDiscipline(parentGroup?.discipline || "");
      setGroupType(parentGroup ? "" : "discipline");
      setParentId(
        parentGroup?.id != null && parentGroup?.id !== ""
          ? String(parentGroup.id)
          : ""
      );
    }
  }, [open, isEdit, group, parentGroup]);

  const parentOptions = useMemo(
    () => validParentOptions(groups, isEdit ? group?.id : null),
    [groups, isEdit, group?.id]
  );

  const numberDisplay = isEdit
    ? (group?.number || "").toString().trim()
    : "";

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t("offers.groups.title_required"));
      return;
    }
    const resolvedParentId =
      parentId === "" || parentId == null ? null : parentId;
    const ok = await onSave?.({
      id: isEdit ? group.id : undefined,
      title: trimmedTitle,
      comment: description.trim(),
      discipline: discipline.trim() || null,
      group_type: groupType.trim() || null,
      parent_id: resolvedParentId,
    });
    if (ok !== false) {
      onClose?.();
    }
  };

  const dialogTitleKey = isEdit
    ? "offers.groups.dialog_title_edit"
    : "offers.groups.dialog_title_create";

  return (
    <PMDialog
      open={open}
      onClose={saving ? () => {} : onClose}
      title={dialogTitleKey}
      size="md"
      closeOnOutsideClick={!saving}
      zIndex={zIndex}
    >
      <form className="offer-calc-group-dialog" onSubmit={handleSubmit}>
        {numberDisplay ? (
          <div className="offer-calc-group-dialog__field">
            <label className="offer-calc-group-dialog__label">
              {t("offers.groups.field_number")}
            </label>
            <div className="offer-calc-group-dialog__readonly" aria-live="polite">
              {numberDisplay}
            </div>
          </div>
        ) : null}

        <div className="offer-calc-group-dialog__field">
          <label className="offer-calc-group-dialog__label" htmlFor="ocg-title">
            {t("offers.groups.field_title")}
          </label>
          <input
            id="ocg-title"
            type="text"
            className="offer-calc-group-dialog__input"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (error) setError("");
            }}
            disabled={saving}
            autoFocus
            autoComplete="off"
          />
        </div>

        <div className="offer-calc-group-dialog__field">
          <label
            className="offer-calc-group-dialog__label"
            htmlFor="ocg-description"
          >
            {t("offers.groups.field_description")}
          </label>
          <textarea
            id="ocg-description"
            className="offer-calc-group-dialog__textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="offer-calc-group-dialog__field">
          <label
            className="offer-calc-group-dialog__label"
            htmlFor="ocg-discipline"
          >
            {t("offers.groups.field_discipline")}
          </label>
          <input
            id="ocg-discipline"
            type="text"
            className="offer-calc-group-dialog__input"
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            disabled={saving}
            autoComplete="off"
            placeholder={t("offers.groups.discipline")}
          />
        </div>

        <div className="offer-calc-group-dialog__field">
          <label
            className="offer-calc-group-dialog__label"
            htmlFor="ocg-parent"
          >
            {t("offers.groups.move_to")}
          </label>
          <select
            id="ocg-parent"
            className="offer-calc-group-dialog__select"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            disabled={saving}
          >
            {parentOptions.map((opt) => (
              <option
                key={opt.value == null ? "root" : opt.value}
                value={opt.value == null ? "" : opt.value}
              >
                {opt.labelKey
                  ? t(opt.labelKey)
                  : opt.label ||
                    formatGroupLabel(opt.group, t("offers.groups.untitled"))}
              </option>
            ))}
          </select>
          <p className="offer-calc-group-dialog__hint">
            {t("offers.groups.field_parent")}
          </p>
        </div>

        <div className="offer-calc-group-dialog__field">
          <label
            className="offer-calc-group-dialog__label"
            htmlFor="ocg-group-type"
          >
            {t("offers.groups.field_group_type")}
          </label>
          <select
            id="ocg-group-type"
            className="offer-calc-group-dialog__select"
            value={groupType}
            onChange={(e) => setGroupType(e.target.value)}
            disabled={saving}
          >
            <option value="">{t("offers.groups.group_type_default")}</option>
            <option value="discipline">
              {t("offers.groups.group_type_discipline")}
            </option>
            <option value="section">
              {t("offers.groups.group_type_section")}
            </option>
          </select>
        </div>

        {error ? (
          <p className="offer-calc-group-dialog__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="offer-calc-group-dialog__actions">
          <button
            type="button"
            className="offer-calc-group-dialog__btn offer-calc-group-dialog__btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            {t("offers.groups.cancel")}
          </button>
          <button
            type="submit"
            className="offer-calc-group-dialog__btn offer-calc-group-dialog__btn--primary"
            disabled={saving}
          >
            {saving ? t("offers.groups.saving") : t("offers.groups.save")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
}
